// Chasms (docs/164 ET7, the cavern's and the Underdark's boards): the kit's dark seen from above, a far wall
// going down under the north edge, a lit near rim on the south, and the ground's own fray hanging over every
// rim (a drop ranks lowest of all, ET18). The colours are the kit's where its notes give them.
import { Canvas, hex, type RGBA } from './canvas';
import { fromOklab, oklab } from './colour';
import { C, type Ctx } from './context';
import type { Ground } from './ground';
import { EDGE_DEPTHS } from '../../../ui/screens/map/groundEdges';

/** The abyss, its far specks and its far wall from the rim down (docs/166 C2: the Underdark, the Ashlands). */
const CHASMS: Record<string, { abyss: string; specks: string[]; wall: string[] }> = {
  underdark: { abyss: '#050509', specks: ['#0c0c14', '#1a1a26'], wall: ['#2a2c36', '#23252e', '#1a1a26', '#17181f', '#0c0c14'] },
  ashlands: { abyss: '#121011', specks: ['#2e0f0c'], wall: ['#3a3638', '#2a2526', '#1e1a1b', '#121011'] },
};

export function paintChasms(ctx: Ctx, out: Canvas, grounds: Array<Ground | undefined>): void {
  const { sq } = ctx, { w, h } = sq;
  const chasm = (x: number, y: number): boolean => sq.at(x, y) === 'chasm' && !((sq.tile(x, y)?.elev ?? 0) > 0);
  // the far wall's hue is the floor's own, darkened (the cavern's `lab(.2 - k * .012)` on its floor's tint)
  const floor = ctx.all('floor')[0];
  let a = -0.003, b = -0.013;
  if (floor) { const i = (12 * floor.w + 12) * 4; [, a, b] = oklab([floor.px[i]!, floor.px[i + 1]!, floor.px[i + 2]!, 255]); a *= 0.5; b *= 0.5; }
  const own = CHASMS[ctx.set.kit];
  const abyss: RGBA = own ? hex(own.abyss) : [7, 9, 13, 255];
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (!chasm(x, y)) continue;
    for (let j = 0; j < C; j++) for (let i = 0; i < C; i++) {
      const r = ctx.roll('speck', x * C + i, y * C + j) % 400;
      out.set(x * C + i, y * C + j, own && r < own.specks.length ? hex(own.specks[r]!) : abyss);
    }
    const SIDES: Array<['n' | 's' | 'w' | 'e', number, number]> = [['n', 0, -1], ['s', 0, 1], ['w', -1, 0], ['e', 1, 0]];
    for (const [s, dx, dy] of SIDES) {
      if (!sq.on(x + dx, y + dy) || chasm(x + dx, y + dy) || sq.solid(x + dx, y + dy)) continue;
      const n = grounds[(y + dy) * w + x + dx];
      const prof = EDGE_DEPTHS[ctx.roll(`rim:${s}`, x, y) % EDGE_DEPTHS.length]!;
      for (let i = 0; i < C; i++) {
        const lip = prof[i]!, deep = lip + (s === 'n' ? 5 : 0);
        for (let k = 0; k < deep; k++) {
          const [ix, iy] = s === 'n' ? [i, k] : s === 's' ? [i, C - 1 - k] : s === 'w' ? [k, i] : [C - 1 - k, i];
          let c: RGBA;
          if (s === 'n' && k >= lip - 1) c = own ? hex(own.wall[Math.min(own.wall.length - 1, k - lip + 1)]!) : fromOklab(0.2 - (k - lip + 1) * 0.012, a, b);   // the far wall going down
          else if (s === 's' && k === lip - 1) c = fromOklab(0.42, a, b);   // the near rim, lit
          else c = n ? Canvas.sample(n.piece, ix, iy, n.turn) : abyss;   // the ground's fray over the rim
          out.set(x * C + ix, y * C + iy, c);
        }
      }
    }
  }
}
